import { z } from 'zod';
/**
 * Statuts d'une commande — `docs/specs-v2/01-flux-fonctionnels.md` §0.
 * Machine à états : `03-donnees-et-configuration.md` §3.
 *
 * Depuis la refonte du 10/09/2026 il n'y a plus qu'**un seul statut** par
 * commande : `integrated` et `prepared` (posés par le Suivi Martelet à partir
 * des retours `OC1`) ont remplacé l'ancien champ `orders.martelet`. Les
 * libellés parlent le vocabulaire du client — les états techniques de la
 * chaîne d'envoi (planifiée, en préparation, générée, en file) portent tous le
 * même libellé « À transmettre ».
 */
export const ORDER_STATUSES = [
    'scheduled',
    'preparing',
    'generated',
    'shadow',
    'queued',
    'sent',
    'integrated',
    'prepared',
    'shipped',
    'error',
    'blocked',
    'handled',
    'ignored',
];
export const orderStatusSchema = z.enum(ORDER_STATUSES);
/** Tones follow `02-ecrans.md` §0: gris (à transmettre, ignorée), bleu (transmise,
 *  en préparation, préparée, simulée), vert (expédiée), orange (traitée manuellement),
 *  rouge (en erreur, bloquée). */
export const ORDER_STATUS = {
    scheduled: {
        code: 'scheduled',
        label: 'À transmettre',
        tone: 'gray',
        description: 'Reçue, envoi prévu à la date de paiement plus l’offset.',
    },
    preparing: {
        code: 'preparing',
        label: 'À transmettre',
        tone: 'gray',
        description: 'Préparation du CSV en cours.',
    },
    generated: {
        code: 'generated',
        label: 'À transmettre',
        tone: 'gray',
        description: 'CSV produit, envoi FTP en attente ou en cours.',
    },
    shadow: {
        code: 'shadow',
        label: 'Simulée',
        tone: 'blue',
        description: 'CSV produit, non envoyé car la boutique est en mode simulation.',
    },
    queued: {
        code: 'queued',
        label: 'À transmettre',
        tone: 'gray',
        description: 'CSV produit, envoi différé car les envois vers Martelet sont suspendus.',
    },
    sent: {
        code: 'sent',
        label: 'Transmise',
        tone: 'blue',
        description: 'CSV déposé sur le FTP Martelet.',
    },
    integrated: {
        code: 'integrated',
        label: 'En préparation',
        tone: 'blue',
        description: 'Martelet a intégré la commande dans son système.',
    },
    prepared: {
        code: 'prepared',
        label: 'Préparée',
        tone: 'blue',
        description: 'Martelet a préparé le colis ; expédition à venir.',
    },
    shipped: {
        code: 'shipped',
        label: 'Expédiée',
        tone: 'green',
        description: 'Colis expédié, numéro de suivi remonté dans Shopify.',
    },
    error: {
        code: 'error',
        label: 'En erreur',
        tone: 'red',
        description: 'Échec de préparation ou d’envoi après épuisement des tentatives.',
    },
    blocked: {
        code: 'blocked',
        label: 'Bloquée',
        tone: 'red',
        description: 'Traitement manuel requis (abonnement à composer, encodage invalide).',
    },
    handled: {
        code: 'handled',
        label: 'Traitée manuellement',
        tone: 'orange',
        description: 'Commande bloquée que l’utilisateur a déclarée traitée hors plateforme.',
    },
    ignored: {
        code: 'ignored',
        label: 'Ignorée',
        tone: 'gray',
        description: 'Non envoyée volontairement (annulée, remboursée, déjà expédiée, ignorée).',
    },
};
/** Statuts qui demandent une action de l’utilisateur (badge du menu « Commandes »). */
export const ORDER_STATUSES_NEEDING_ATTENTION = ['error', 'blocked'];
/** Les états de la chaîne d'envoi, avant dépôt du CSV — tous « À transmettre ». */
export const ORDER_STATUSES_TO_TRANSMIT = [
    'scheduled',
    'preparing',
    'generated',
    'queued',
];
/** Les états postérieurs au dépôt du CSV chez Martelet. */
export const ORDER_STATUSES_TRANSMITTED = [
    'sent',
    'integrated',
    'prepared',
    'shipped',
];
//# sourceMappingURL=order.js.map